/**
 * Guard around the Builder SDK's `builder.userAttributes` cookie.
 *
 * The SDK's `userAttributesService` (`@builder.io/sdk-react@4.2.4`) round-trips
 * that cookie through two unsafe halves:
 *
 *   setter: `document.cookie = `${name}=${value}; …``   // raw JSON.stringify, NO encodeURIComponent
 *   getter: `document.cookie.split("; ").find(…)?.split("=")[1]`
 *   read:   `JSON.parse(getCookieSync(…) || "{}")`      // NO try/catch
 *
 * So the stored value contains literal `"`/`{`/`}`/`,`/spaces and any non-ASCII
 * character a trait value carries, and the getter additionally truncates at the
 * first `=` inside the value. Either way `JSON.parse` can receive a value cut
 * mid-string-literal and throws `SyntaxError` with no guard.
 *
 * That matters here because we feed the cookie Segment traits
 * (`mapTraitsToBuilderAttributes` → `builderUserAttributes` in `app/root.tsx`),
 * and FSA's house brand arrives as `topBrowsedBrand: "Caring Mill™"` — a raw
 * U+2122 in the cookie value. Clipping there leaves an odd number of quotes,
 * i.e. `JSON Parse error: Unterminated string`, which was ~1,550 Safari/iOS
 * sessions a day on fsastore.com.
 *
 * `setUserAttributes` MERGES onto the existing value, so it reads the cookie
 * before every write — and it's called from a `useEffect` in the root `App()`
 * component, whose throw React routes to `RootErrorBoundary`. One bad cookie
 * therefore blanked the whole storefront on every page view until the cookie
 * was cleared, which nothing did.
 *
 * This module carries three halves of the fix:
 *
 *   1. purge/safe-set containment (#899 — kept permanently as the regression
 *      detector)
 *   2. COM-123 write-side sanitizer — `sanitizeBuilderAttributeValue` strips
 *      cookie-hostile characters at the producers so the SDK is never handed
 *      a value its round-trip cannot survive
 *   3. COM-101 parse-time cookie write — `buildBuilderUserAttributesInlineJs`
 *      so the cookie exists BEFORE the SDK's inline personalization scripts
 *      (`content-variants-*.js`) run. Those scripts read the cookie during
 *      HTML parse (before hydration, before the root `useEffect` setter) and
 *      hide non-winning variants. An empty cookie on first paint is why
 *      data-bound Symbols reverted to defaults on Safari. The snippet MERGES
 *      the way the SDK setter does (existing keys kept, `attrRemovals`
 *      deleted, fresh keys win) — a plain overwrite would break the reconcile
 *      contract in builder-attrs-reconcile.server.ts: martech-written keys
 *      and keep-if-live assignments must survive a document load.
 *
 * HTTP `Set-Cookie` is deliberately not used: the SDK stores raw
 * `JSON.stringify` (`"` and `,` are not RFC 6265 cookie-octets), which
 * Safari will reject as a response header, and a visitor-specific
 * `Set-Cookie` would refuse the guest HTML cache store.
 */
import { safeJsonForScript } from "~/hec/analytics/safe-json";
import { reportClientError } from "~/utils/report-client-error";

export const BUILDER_USER_ATTRIBUTES_COOKIE = "builder.userAttributes";

/**
 * Window global the parse-time snippet sets (to the truncated cookie length)
 * when it had to replace an unparseable cookie. The snippet runs before
 * hydration, so `purgeCorruptBuilderUserAttributes` can no longer observe the
 * corrupt value itself — it consumes this marker instead and files the same
 * report, keeping the regression detector alive.
 */
export const BUILDER_ATTRS_CORRUPT_MARKER = "__BUILDER_ATTRS_CORRUPT";

/**
 * Every character the SDK's cookie round-trip cannot survive (COM-123 — the
 * write-side root-cause fix; the purge below stays as the regression detector):
 *
 *   - `=`           the getter's `.split("=")[1]` truncates at the first one
 *   - `;` `,`       cookie-attribute / cookie-list separators (RFC 6265)
 *   - `"` `\`       re-quoted unescaped inside the raw JSON.stringify write
 *   - control chars rejected or mangled by cookie parsers
 *   - non-ASCII     WebKit clips the stored value at the first byte > 0x7F
 *                   (FSA's `topBrowsedBrand: "Caring Mill™"`, U+2122)
 *
 * STRIPPED, not encoded: an encoding would have to be reversed by the SDK's
 * reader, which we don't control. Consequence for martech: Builder targeting
 * rules must be authored against the stripped value — "Caring Mill", no ™.
 *
 * `\u007F-\uFFFF` covers DEL plus all non-ASCII: JS regexes without the `u`
 * flag match UTF-16 code units, so astral characters (each two surrogates in
 * that range) are stripped too.
 *
 * NOTE `|` is deliberately NOT in this set — it is cookie-legal, survives the
 * SDK round-trip, and is the join delimiter for the `hecTraitKeys` /
 * `hecCampaignFlagKeys` ledgers (builder-attrs-reconcile.server.ts), which
 * ride this cookie and must round-trip through this sanitizer intact.
 */
// eslint-disable-next-line no-control-regex
const COOKIE_UNSAFE_CHARS = /[=;,"\\\u0000-\u001F\u007F-\uFFFF]/g;

/**
 * Make a string safe to round-trip through the Builder SDK's unencoded
 * `builder.userAttributes` cookie write / `split("=")[1]` read / unguarded
 * `JSON.parse`. Applied at the producers (`mapTraitsToBuilderAttributes`,
 * `flattenABTastyVariations`, `buildCampaignFlagAttributes`) and as a belt
 * at the `app/root.tsx` merge point — Segment traits, AB Tasty
 * campaign/variation ids + names + flag values, price-test fields.
 */
export function sanitizeBuilderAttributeValue(value: string): string {
  return value.replace(COOKIE_UNSAFE_CHARS, "").trim();
}

/**
 * Same character class, applied to attribute KEYS. Keys are an unbounded
 * source too (AB Tasty flag names arrive verbatim via `snakeToCamel`), and a
 * `=` in a key truncates the SDK's `split("=")[1]` read exactly like one in a
 * value. Keys are DROPPED rather than renamed: a renamed key silently stops
 * matching whatever targeting rule was authored against it, whereas a dropped
 * one is a visible authoring error. Non-global copy — `.test()` on a `g`
 * regex is stateful.
 */
const COOKIE_UNSAFE_KEY = new RegExp(COOKIE_UNSAFE_CHARS.source);

/**
 * Sanitize all string values of a Builder userAttributes object and drop any
 * key the cookie round-trip cannot survive (see `COOKIE_UNSAFE_KEY`).
 * Booleans pass through untouched (nothing cookie-hostile in `true`/`false`),
 * and so do `undefined` deletion markers (the reconcile's attrRemovals — see
 * `safeSetClientUserAttributes` below).
 */
export function sanitizeBuilderAttributes<
  T extends Record<string, string | boolean | undefined>,
>(attributes: T): T {
  const out: Record<string, string | boolean | undefined> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (COOKIE_UNSAFE_KEY.test(key)) continue;
    out[key] =
      typeof value === "string" ? sanitizeBuilderAttributeValue(value) : value;
  }
  return out as T;
}

/**
 * Read the cookie EXACTLY the way the SDK's `getCookieSync` does — including
 * the `.split("=")[1]` truncation. Reading it any more correctly would defeat
 * the purpose: we need to see the same string the SDK is about to choke on, not
 * the one it should have written.
 */
function readCookieAsSdkDoes(): string | undefined {
  return document.cookie
    .split("; ")
    .find(c => c.startsWith(`${BUILDER_USER_ATTRIBUTES_COOKIE}=`))
    ?.split("=")[1];
}

/**
 * Mirrors the SDK's `domain` derivation (`pt` in the bundle): keep a 2-label
 * host as-is, otherwise drop the leading label. `www.fsastore.com` →
 * `fsastore.com`, `fsastore.com` → `fsastore.com`. The delete has to match the
 * domain the SDK wrote with, or the browser keeps the corrupt cookie.
 */
export function sdkCookieDomain(hostname: string): string {
  if (hostname === "localhost" || hostname === "127.0.0.1") return hostname;
  const labels = hostname.split(".");
  return labels.length > 2 ? labels.slice(1).join(".") : hostname;
}

/**
 * The `document.cookie` assignment the SDK's setter emits (`bt` in
 * `@builder.io/sdk-react@4.2.4`): raw value, `path=/`, derived `domain`,
 * and on https `secure; SameSite=None`. No `expires` — session cookie, same
 * as `setUserAttributes` (it never passes one). Byte-match this when writing
 * from the parse-time inline script so we don't mint a second host-only
 * cookie next to the SDK's domain-scoped one.
 */
export function buildSdkUserAttributesCookie(
  value: string,
  { hostname, isHttps }: { hostname: string; isHttps: boolean },
): string {
  const parts = [
    `${BUILDER_USER_ATTRIBUTES_COOKIE}=${value}`,
    "path=/",
    `domain=${sdkCookieDomain(hostname)}`,
  ];
  if (isHttps) {
    parts.push("secure", "SameSite=None");
  }
  return parts.join("; ");
}

/**
 * Parse-time inline script (COM-101) that writes `builder.userAttributes`
 * the same way the SDK's setter does, so the cookie exists before the SDK's
 * inlined personalization scripts (`getPersonalizedVariant` /
 * `updateVisibilityStylesScript`) read it during HTML parse.
 *
 * Same semantics as the post-hydration `safeSetClientUserAttributes` call,
 * just earlier: read the current cookie the way the SDK's getter does
 * (`split("; ")` + `split("=")[1]`, so it chokes on exactly the values the
 * SDK would), treat an unparseable / non-object value as empty and leave the
 * truncated length on `BUILDER_ATTRS_CORRUPT_MARKER` for the purge to
 * report, delete `removals` (the reconcile's dead keys), then copy the fresh
 * attributes over. Keys the server does not emit — martech-written,
 * keep-if-live assignments — survive untouched.
 *
 * EVERY `builder.userAttributes` entry the browser exposes is merged, in
 * order (later wins): a host-only twin and the SDK's domain-scoped cookie
 * both appear in `document.cookie`, and browsers disagree on which comes
 * first (Safari lists the domain cookie first), so reading only the first
 * match would drop whichever one lost the ordering.
 *
 * Values are sanitized and hostile keys dropped first (COM-123) — the root
 * merge already does this, this is the belt so a future caller cannot hand
 * the snippet a ™/`=`. `safeJsonForScript` escapes the literals for the
 * `<script>` body; the snippet then `JSON.stringify`s the merged object for
 * the cookie value, matching the SDK write.
 *
 * Before the domain-scoped write, a host-only twin (an older build's raw
 * `document.cookie` write, or any third-party write without `domain`) is
 * expired: browsers order it first, so it would shadow the fresh value for
 * both the SDK getter and the personalization scripts' `getCookie`. Its
 * keys were already merged above. Same threat model as `expireCookie`.
 * Skipped where the SDK's domain equals the hostname (localhost, 2-label
 * hosts) — nothing to shadow there, and a rejected domain write must not
 * leave the visitor with no cookie at all.
 *
 * ES5 on purpose: this executes as a parser script, before the app bundle.
 */
export function buildBuilderUserAttributesInlineJs(
  attributes: Record<string, string | boolean>,
  removals: readonly string[] = [],
): string {
  const sanitized = sanitizeBuilderAttributes(attributes);
  const attrsLiteral = safeJsonForScript(sanitized);
  const removalsLiteral = safeJsonForScript(removals);
  const cookieName = JSON.stringify(BUILDER_USER_ATTRIBUTES_COOKIE);
  const marker = JSON.stringify(BUILDER_ATTRS_CORRUPT_MARKER);
  return (
    "(function(){try{" +
    `var a=${attrsLiteral},r=${removalsLiteral},n=${cookieName},m=${marker};` +
    'var e=document.cookie.split("; "),c={},i,k,v,o;' +
    'for(i=0;i<e.length;i++){if(e[i].indexOf(n+"=")!==0)continue;v=e[i].split("=")[1]||"";if(!v)continue;' +
    "try{o=JSON.parse(v);}catch(x){window[m]=v.length;continue;}" +
    'if(Object.prototype.toString.call(o)!=="[object Object]")continue;' +
    "for(k in o)if(Object.prototype.hasOwnProperty.call(o,k))c[k]=o[k];}" +
    "for(i=0;i<r.length;i++)delete c[r[i]];" +
    "for(k in a)if(Object.prototype.hasOwnProperty.call(a,k))c[k]=a[k];" +
    "var h=location.hostname;" +
    'var d=(h==="localhost"||h==="127.0.0.1")?h:(h.split(".").length>2?h.split(".").slice(1).join("."):h);' +
    'if(d!==h)document.cookie=n+"=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";' +
    'var p=[n+"="+JSON.stringify(c),"path=/","domain="+d];' +
    'if(location.protocol==="https:"){p.push("secure");p.push("SameSite=None");}' +
    'document.cookie=p.join("; ");' +
    "}catch(err){}})();"
  );
}

/**
 * File the report for a corrupt cookie the parse-time snippet already
 * replaced (see `BUILDER_ATTRS_CORRUPT_MARKER`). Same scope as the direct
 * purge so the existing signal keeps counting; `source` tells the two apart.
 */
function consumeInlineCorruptMarker(): boolean {
  if (typeof window === "undefined") return false;
  const globals = window as unknown as Record<string, unknown>;
  const truncatedLength = globals[BUILDER_ATTRS_CORRUPT_MARKER];
  if (typeof truncatedLength !== "number") return false;
  delete globals[BUILDER_ATTRS_CORRUPT_MARKER];
  reportClientError(
    "builder_user_attributes_corrupt",
    new SyntaxError("replaced by the parse-time inline script"),
    { truncatedLength, source: "inline" },
  );
  return true;
}

function expireCookie(): void {
  const expiry = "Thu, 01 Jan 1970 00:00:00 GMT";
  const base = `${BUILDER_USER_ATTRIBUTES_COOKIE}=; expires=${expiry}; path=/`;
  // Delete both with and without `domain`. The SDK always sets `domain`, but a
  // value written by an older build (or by the pre-SDK manual `document.cookie`
  // write this replaced) may be host-only, and a host-only cookie is not
  // removed by a domain-scoped delete.
  document.cookie = base;
  document.cookie = `${base}; domain=${sdkCookieDomain(window.location.hostname)}`;
}

/**
 * Delete `builder.userAttributes` if the SDK would throw reading it back.
 * Returns true when a corrupt cookie was purged — or was already replaced by
 * the parse-time snippet, which hands the observation over via
 * `BUILDER_ATTRS_CORRUPT_MARKER` since it runs before this can look.
 *
 * An absent or empty cookie is fine — the SDK's `|| "{}"` fallback covers it,
 * so there is nothing to purge and nothing to report.
 */
export function purgeCorruptBuilderUserAttributes(): boolean {
  if (typeof document === "undefined") return false;

  const inlineReplaced = consumeInlineCorruptMarker();

  let raw: string | undefined;
  try {
    raw = readCookieAsSdkDoes();
  } catch {
    // Storage access denied (Safari private browsing / blocked cookies). The
    // SDK's own getter try/catches this too, so there is no corruption to fix.
    return inlineReplaced;
  }

  if (!raw) return inlineReplaced;

  try {
    JSON.parse(raw);
    return inlineReplaced;
  } catch (e) {
    expireCookie();
    // Deliberately reported: this is the regression detector for the bug. It
    // should trend to ~0 once attribute values are sanitized upstream, and any
    // resurgence means a new cookie-hostile trait value shipped. `length` only
    // — the value carries shopper traits and must not be logged.
    reportClientError("builder_user_attributes_corrupt", e, {
      truncatedLength: raw.length,
    });
    return true;
  }
}

/**
 * `setClientUserAttributes` with the storefront kept alive. Purges a corrupt
 * cookie first (otherwise the SDK's read-before-merge throws on the value we
 * already know is bad), then contains anything else the SDK throws.
 *
 * Losing these attributes degrades client-side Builder targeting for one page
 * view; it does not affect server-rendered Builder content, which passes
 * `userAttributes` straight to `fetchOneEntry` and never consults this cookie.
 * A blank storefront is categorically worse than stale client-side targeting.
 */
export function safeSetClientUserAttributes(
  // `undefined` values are deliberate: the SDK merges {...current, ...next}
  // and JSON.stringify then OMITS undefined properties — the documented
  // deletion mechanism the root effect's attrRemovals ride on (see
  // builder-attrs-reconcile.server.ts). The guard passes them through
  // untouched.
  attributes: Record<string, string | boolean | undefined>,
  setter: (attrs: Record<string, string | boolean | undefined>) => void,
): void {
  purgeCorruptBuilderUserAttributes();
  try {
    // Belt: sanitize here too so a future caller that skips the root merge
    // wrap cannot hand the SDK a ™/`=` value. Idempotent on already-clean
    // attrs. `undefined` deletion markers pass through untouched.
    setter(sanitizeBuilderAttributes(attributes));
  } catch (e) {
    reportClientError("builder_user_attributes_set", e);
  }
}
